import { useEffect, useState } from 'react';
import { apiFetch } from '../utils/api';

export function useClients({ q = '', placeholder = undefined, page = 1, pageSize = 20, sort = 'nume.asc', reloadKey = 0 } = {}) {
  const [data, setData] = useState({ items: [], page: 1, page_size: 20, total: 0 });
  const [status, setStatus] = useState('idle');
  const [error, setError] = useState(null);

  useEffect(() => {
    const controller = new AbortController();
    async function run() {
      setStatus('loading');
      setError(null);
      try {
        const params = new URLSearchParams();
        if (q) params.set('q', q);
        if (placeholder !== undefined && placeholder !== null) params.set('placeholder', String(placeholder));
        if (page) params.set('page', String(page));
        if (pageSize) params.set('page_size', String(pageSize));
        if (sort) params.set('sort', sort);

        const res = await apiFetch(`/api/clients?${params.toString()}`, { signal: controller.signal });
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          throw new Error(err.msg || 'Eroare la încărcarea clienților.');
        }
        const json = await res.json();
        setData(json);
        setStatus('success');
      } catch (e) {
        if (e.name === 'AbortError') return;
        setError(e.message);
        setStatus('error');
      }
    }
    run();
    return () => controller.abort();
  }, [q, placeholder, page, pageSize, sort, reloadKey]);

  return { data, status, error };
}

export function useClientDetail(clientId) {
  const [data, setData] = useState(null);
  const [status, setStatus] = useState('idle');
  const [error, setError] = useState(null);
  const [refetchTrigger, setRefetchTrigger] = useState(0);

  useEffect(() => {
    if (!clientId) return;
    const controller = new AbortController();
    async function run() {
      setStatus('loading');
      setError(null);
      try {
        const res = await apiFetch(`/api/clients/${clientId}`, { signal: controller.signal });
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          throw new Error(err.msg || 'Eroare la încărcarea clientului.');
        }
        const json = await res.json();
        setData(json);
        setStatus('success');
      } catch (e) {
        if (e.name === 'AbortError') return;
        setError(e.message);
        setStatus('error');
      }
    }
    run();
    return () => controller.abort();
  }, [clientId, refetchTrigger]);

  const refetch = () => setRefetchTrigger(prev => prev + 1);

  return { data, status, error, refetch };
}

export function useClientInvoices(clientId, { page = 1, pageSize = 20 } = {}) {
  const [data, setData] = useState({ items: [], page: 1, page_size: 20, total: 0 });
  const [status, setStatus] = useState('idle');
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!clientId) return;
    const controller = new AbortController();
    async function run() {
      setStatus('loading');
      setError(null);
      try {
        const params = new URLSearchParams({ page: String(page), page_size: String(pageSize) });
        const res = await apiFetch(`/api/clients/${clientId}/invoices?${params.toString()}`, { signal: controller.signal });
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          throw new Error(err.msg || 'Eroare la încărcarea facturilor.');
        }
        const json = await res.json();
        setData(json);
        setStatus('success');
      } catch (e) {
        if (e.name === 'AbortError') return;
        setError(e.message);
        setStatus('error');
      }
    }
    run();
    return () => controller.abort();
  }, [clientId, page, pageSize]);

  return { data, status, error };
}

export function useClientEndUsers(clientId, { page = 1, pageSize = 20 } = {}) {
  const [data, setData] = useState({ items: [], page: 1, page_size: 20, total: 0 });
  const [status, setStatus] = useState('idle');
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!clientId) return;
    const controller = new AbortController();
    async function run() {
      setStatus('loading');
      setError(null);
      try {
        const params = new URLSearchParams({ page: String(page), page_size: String(pageSize) });
        const res = await apiFetch(`/api/clients/${clientId}/end-users?${params.toString()}`, { signal: controller.signal });
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          throw new Error(err.msg || 'Eroare la încărcarea beneficiarilor.');
        }
        const json = await res.json();
        setData(json);
        setStatus('success');
      } catch (e) {
        if (e.name === 'AbortError') return;
        setError(e.message);
        setStatus('error');
      }
    }
    run();
    return () => controller.abort();
  }, [clientId, page, pageSize]);

  return { data, status, error };
}

export function useClientProducts(clientId, { page = 1, pageSize = 20 } = {}) {
  const [data, setData] = useState({ items: [], page: 1, page_size: 20, total: 0 });
  const [status, setStatus] = useState('idle');
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!clientId) return;
    const controller = new AbortController();
    async function run() {
      setStatus('loading');
      setError(null);
      try {
        const params = new URLSearchParams({ page: String(page), page_size: String(pageSize) });
        const res = await apiFetch(`/api/clients/${clientId}/products?${params.toString()}`, { signal: controller.signal });
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          throw new Error(err.msg || 'Eroare la încărcarea produselor.');
        }
        const json = await res.json();
        setData(json);
        setStatus('success');
      } catch (e) {
        if (e.name === 'AbortError') return;
        setError(e.message);
        setStatus('error');
      }
    }
    run();
    return () => controller.abort();
  }, [clientId, page, pageSize]);

  return { data, status, error };
}

export function useEndUserDetail(id) {
  const [data, setData] = useState(null);
  const [status, setStatus] = useState('idle');
  const [error, setError] = useState(null);
  const [refetchTrigger, setRefetchTrigger] = useState(0);

  useEffect(() => {
    if (!id) return;
    const controller = new AbortController();
    async function run() {
      setStatus('loading');
      setError(null);
      try {
        const res = await apiFetch(`/api/end-users/${id}`, { signal: controller.signal });
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          throw new Error(err.msg || 'Eroare la încărcarea beneficiarului.');
        }
        const json = await res.json();
        setData(json);
        setStatus('success');
      } catch (e) {
        if (e.name === 'AbortError') return;
        setError(e.message);
        setStatus('error');
      }
    }
    run();
    return () => controller.abort();
  }, [id, refetchTrigger]);

  const refetch = () => setRefetchTrigger((prev) => prev + 1);

  return { data, status, error, refetch };
}

export function useProductDetail(id) {
  const [data, setData] = useState(null);
  const [status, setStatus] = useState('idle');
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!id) return;
    const controller = new AbortController();
    async function run() {
      setStatus('loading');
      setError(null);
      try {
        const res = await apiFetch(`/api/products/${id}`, { signal: controller.signal });
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          throw new Error(err.msg || 'Eroare la încărcarea produsului.');
        }
        const json = await res.json();
        setData(json);
        setStatus('success');
      } catch (e) {
        if (e.name === 'AbortError') return;
        setError(e.message);
        setStatus('error');
      }
    }
    run();
    return () => controller.abort();
  }, [id]);

  return { data, status, error };
}


export function useClientEndUsersList(clientId) {
  const [data, setData] = useState([]); // The initial state is an empty array
  const [status, setStatus] = useState('idle');
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!clientId) return; // Don't fetch if there's no client ID

    const controller = new AbortController();
    async function run() {
      setStatus('loading');
      setError(null);
      try {
        const res = await apiFetch(`/api/clients/${clientId}/end-users-list`, { signal: controller.signal });
        if (!res.ok) {
          throw new Error('Eroare la încărcarea listei de beneficiari.');
        }
        const json = await res.json();
        setData(json); // The API returns a simple array
        setStatus('success');
      } catch (e) {
        if (e.name === 'AbortError') return;
        setError(e.message);
        setStatus('error');
      }
    }
    run();
    return () => controller.abort();
  }, [clientId]);

  return { data, status, error };
}